// src/api/client.ts
// Thin client for calling the backend with Firebase ID token.
// Source of truth for API_BASE selection so every consumer is consistent.

import { auth } from "firebaseConfig";
import { attachDevAuthHeader, getDevAuthSession } from "devAuth";

// ---- Base URL selection strategy -------------------------------------------
// Single source of truth: REACT_APP_API_BASE. See docs/adr/0001-environment-configuration.md.
const RAW_API_BASE = (process.env.REACT_APP_API_BASE || "").trim();
if (!RAW_API_BASE) {
  throw new Error(
    "Missing required env var: REACT_APP_API_BASE. " +
      `Check .env.${process.env.NODE_ENV} (or .env.${process.env.NODE_ENV}.local).`
  );
}

export const API_BASE = RAW_API_BASE.replace(/\/+$/, "");
export const V1 = `${API_BASE}/v1`;
export const HELLO_URL = `${V1}/hello`;

// ----------------------------------------------------------------------------

export interface UserProfile {
  id: number;
  firebase_id: string;
  email: string;
  display_name?: string | null;
  created_date: string;
  terms_accepted_date: string | null;
  last_login_date: string | null;
  last_updated_date: string;
  lock_version: number;
  is_admin?: boolean;
  is_superadmin?: boolean;
}

// Shared question types
export type QuestionnaireQuestion = {
  id: string;
  label: string;
  type:
    | "text"
    | "select"
    | "date"
    | "email"
    | "checkbox"
    | "multi_select"
    | "slider"
    | "number"
    | "static_text";
  options?: string[] | null;
  sub_label?: string | null;
  body?: string | null;
  exclusive_options?: string[] | null;
  required: boolean;
};

export type SectionCopy = {
  headline?: string | null;
  body?: string | null;
  cta_label?: string | null;
};

export interface QuestionnaireOut {
  auth_questions: QuestionnaireQuestion[];
  general_questions: QuestionnaireQuestion[];
  responses: Record<string, any>;
  last_answered_at: string | null;
  lock_version: number;
  status: "pending" | "complete";
  requires_auth_info: boolean;
  skipped_at?: string | null;
  completed_at?: string | null;

  // section-level copy keyed by category key (e.g. "data_consent", "symptoms")
  section_copy?: Record<string, SectionCopy>;
}

/**
 * Single source of truth for authenticated fetches.
 * Adds Firebase ID token, standardizes error shape, and returns parsed JSON (or undefined for empty).
 *
 * ✅ UPDATED: Now intelligently handles FormData (no forced JSON Content-Type)
 */
export async function authFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});

  const isFormData =
    typeof FormData !== "undefined" && init.body instanceof FormData;

  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const devSession = getDevAuthSession();
  if (devSession) {
    attachDevAuthHeader(headers, devSession);
  } else {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const idToken = await currentUser.getIdToken(/* forceRefresh */ true);
    headers.set("Authorization", `Bearer ${idToken}`);
  }

  const res = await fetch(url, {
    ...init,
    headers,
  });

  if (!res.ok) {
    let detail: any = null;
    try {
      const parsed = await res.json();
      detail = parsed?.detail ?? parsed;
    } catch {
      /* ignore non-JSON bodies */
    }
    const error: any = new Error(`HTTP ${res.status}`);
    error.status = res.status;
    error.detail = detail;
    throw error;
  }

  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as unknown as T);
}

export async function anonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers || {}),
      "Content-Type": "application/json",
    },
  });

  if (!res.ok) {
    let detail: any = null;
    try {
      const parsed = await res.json();
      detail = parsed?.detail ?? parsed;
    } catch {
      /* ignore non-JSON bodies */
    }
    const error: any = new Error(`HTTP ${res.status}`);
    error.status = res.status;
    error.detail = detail;
    throw error;
  }

  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as unknown as T);
}

/* ====== Users ====== */
export async function apiRegister(consent: boolean): Promise<UserProfile> {
  return authFetch<UserProfile>(`${V1}/users/me/register`, {
    method: "POST",
    body: JSON.stringify({ consent }),
  });
}

export async function apiLogin(): Promise<UserProfile> {
  return authFetch<UserProfile>(`${V1}/users/me/login`, { method: "POST" });
}

export async function apiGetProfile(): Promise<UserProfile> {
  return authFetch<UserProfile>(`${V1}/users/me/profile`, { method: "GET" });
}

export async function apiUpdateProfile(payload: {
  display_name?: string | null;
  expected_lock_version?: number;
}): Promise<UserProfile> {
  return authFetch<UserProfile>(`${V1}/users/me/profile`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/* ====== Admin (whoami passthrough for gating fallback) ====== */
export async function apiAdminWhoAmI(): Promise<{ is_admin: boolean; is_superadmin: boolean }> {
  return authFetch<{ is_admin: boolean; is_superadmin: boolean }>(`${V1}/admin/whoami`, { method: "GET" });
}

/* ====== Questionnaire (under users domain) ====== */
export async function apiGetQuestionnaire(): Promise<QuestionnaireOut> {
  return authFetch<QuestionnaireOut>(`${V1}/users/me/questionnaire`, {
    method: "GET",
  });
}

export async function apiSaveQuestionnaire(args: {
  responses?: Record<string, any>;
  expected_lock_version?: number;
  section?: "auth" | "general" | "all";
  skip?: boolean;
}): Promise<QuestionnaireOut> {
  return authFetch<QuestionnaireOut>(`${V1}/users/me/questionnaire`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}